/**
 * WeatherDetailsTab
 *
 * This is the main container component for the weather monitoring system, managing the overall
 * weather data display and tab navigation for power grid towers.
 *
 * Main Features:
 * 1. Weather Data Management:
 *    - Real-time weather data fetching from Tomorrow.io API
 *    - Automatic data refresh (5-minute intervals)
 *    - Manual refresh capability
 *    - Fallback to simulated data if API fails
 *
 * 2. Tab Navigation:
 *    - Overview: Current weather conditions and key metrics
 *    - Forecast: 12-hour weather predictions
 *    - Alerts: Weather warnings and alerts
 *    - Summary: Tabular view of all towers' weather data
 *
 * 3. Tower Selection:
 *    - Automatic selection based on tree view
 *    - Manual tower selection
 *    - Persistent tower selection across tabs
 */

"use client"

import React, { useState, useEffect, useCallback } from "react"
import { View, StyleSheet } from "react-native"
import { Text, useTheme, ActivityIndicator, Button, SegmentedButtons } from "react-native-paper"
import type { AssetNode, ProjectData } from "../../../types/project"
import { WeatherOverviewTab } from "./WeatherOverviewTab"
import { WeatherForecastTab } from "./WeatherForecastTab"
import { WeatherAlertsTab } from "./WeatherAlertsTab"
import { WeatherSummaryTab } from "./WeatherSummaryTab"
import { RefreshCw } from "lucide-react"
import Constants from "expo-constants"

interface WeatherDetailsTabProps {
  selectedNode?: AssetNode | null
  projectData?: ProjectData | null
  menuAction?: { type: string; visible: boolean }
}

export const WeatherDetailsTab: React.FC<WeatherDetailsTabProps> = ({ selectedNode, projectData, menuAction }) => {
  const theme = useTheme()
  const [activeTab, setActiveTab] = useState("overview")
  const [isLoading, setIsLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [weatherData, setWeatherData] = useState<any>(null)
  const [forecastData, setForecastData] = useState<any[]>([])
  const [selectedTower, setSelectedTower] = useState<any>(null)
  const [isSimulatedData, setIsSimulatedData] = useState(false)

  // Extract tower data from project data
  const towers = React.useMemo(() => {
    if (!projectData) return []

    const extractedTowers: any[] = []

    const extractTowers = (node: AssetNode, parent?: { id: string; name: string; type: string }) => {
      if (node.type === "tower") {
        const lat = Number.parseFloat(node.properties.latitude || node.properties.Latitude || "0")
        const lng = Number.parseFloat(node.properties.longitude || node.properties.Longitude || "0")

        if (!isNaN(lat) && !isNaN(lng)) {
          extractedTowers.push({
            id: node.name,
            name: node.name,
            lat,
            lng,
            properties: node.properties,
            type: node.type,
            parent,
          })
        }
      }

      // Process children recursively
      if (node.children) {
        node.children.forEach((child) => {
          extractTowers(child, node.type === "section" ? { id: node.name, name: node.name, type: node.type } : parent)
        })
      }
    }

    // Start extraction from the root node
    if (projectData.Lines) {
      extractTowers(projectData.Lines)
    }

    return extractedTowers
  }, [projectData])

  // Set selected tower based on selectedNode
  useEffect(() => {
    if (selectedNode && selectedNode.type === "tower" && towers.length > 0) {
      const tower = towers.find((t) => t.name === selectedNode.name)
      if (tower) {
        setSelectedTower(tower)
        fetchWeatherData(tower.lat, tower.lng)
      }
    } else if (towers.length > 0) {
      // Default to first tower if no tower is selected
      setSelectedTower(towers[0])
      fetchWeatherData(towers[0].lat, towers[0].lng)
    }
  }, [selectedNode, towers])

  // Fetch weather data for a specific location
  const fetchWeatherData = useCallback(async (latitude: number, longitude: number) => {
    if (!latitude || !longitude) return

    setIsLoading(true)
    setIsSimulatedData(false) // Reset simulation flag
    try {
      // Get API key from Expo Constants
      const API_KEY = Constants.expoConfig?.extra?.TOMORROW_IO_API_KEY

      // Fetching weather data from Tomorrow.io API
      // Fetch real-time weather data
      const realtimeResponse = await fetch(
        `https://api.tomorrow.io/v4/weather/realtime?location=${latitude},${longitude}&apikey=${API_KEY}`,
      )

      // Fetch forecast data
      const forecastResponse = await fetch(
        `https://api.tomorrow.io/v4/weather/forecast?location=${latitude},${longitude}&apikey=${API_KEY}&timesteps=1h`,
      )

      if (!realtimeResponse.ok || !forecastResponse.ok) {
        // API calls failed, using simulated data
        simulateWeatherData(latitude, longitude)
        return
      }

      const realtimeData = await realtimeResponse.json()
      const forecastData = await forecastResponse.json()

      setWeatherData(realtimeData)
      setForecastData(forecastData.timelines.hourly.slice(0, 12))
      setLastUpdated(new Date())
      setIsLoading(false)
    } catch (error) {
      // Error fetching weather data
      simulateWeatherData(latitude, longitude)
    }
  }, [])

  // Simulate weather data for demo or fallback purposes
  const simulateWeatherData = (latitude: number, longitude: number) => {
    // Using simulated weather data due to API failure
    setIsSimulatedData(true)
    // Simulate real-time weather data
    const simulatedWeatherData = {
      data: {
        values: {
          temperature: Math.round(15 + Math.random() * 15),
          windSpeed: Math.round(5 + Math.random() * 30),
          humidity: Math.round(40 + Math.random() * 50),
          precipitationIntensity: Math.round(Math.random() * 10) / 10,
          lightningStrikeCount: Math.random() > 0.8 ? Math.round(Math.random() * 5) : 0,
          weatherCode: Math.floor(Math.random() * 4000) + 1000,
        },
      },
    }

    // Simulate forecast data
    const simulatedForecastData = Array.from({ length: 12 }, (_, i) => ({
      time: new Date(Date.now() + i * 3600 * 1000).toISOString(),
      values: {
        temperature: Math.round(15 + Math.random() * 15 + i * 0.5 * (Math.random() > 0.5 ? 1 : -1)),
        windSpeed: Math.round(5 + Math.random() * 30 + i * 0.3 * (Math.random() > 0.5 ? 1 : -1)),
        humidity: Math.round(40 + Math.random() * 50),
        precipitationIntensity: Math.round(Math.random() * 10) / 10,
        lightningStrikeCount: Math.random() > 0.9 ? Math.round(Math.random() * 3) : 0,
      },
    }))

    setWeatherData(simulatedWeatherData)
    setForecastData(simulatedForecastData)
    setLastUpdated(new Date())
    setIsLoading(false)
  }

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    if (selectedTower) {
      fetchWeatherData(selectedTower.lat, selectedTower.lng)
    }
  }, [selectedTower, fetchWeatherData])

  // Set up automatic refresh every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(
      () => {
        if (selectedTower) {
          fetchWeatherData(selectedTower.lat, selectedTower.lng)
        }
      },
      5 * 60 * 1000,
    ) // 5 minutes

    return () => clearInterval(intervalId)
  }, [selectedTower, fetchWeatherData])

  // Handle tower selection change
  const handleTowerChange = useCallback(
    (tower: any) => {
      setSelectedTower(tower)
      fetchWeatherData(tower.lat, tower.lng)
    },
    [fetchWeatherData],
  )

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <View>
          <Text variant="headlineMedium">Weather Details</Text>
          {isSimulatedData && (
            <Text
              variant="bodyMedium"
              style={{
                color: theme.colors.error,
                marginTop: 4,
              }}
            >
              ⚠️ Using simulated data - Unable to fetch real-time weather data
            </Text>
          )}
        </View>
        <View style={styles.headerRight}>
          {selectedTower && (
            <Text variant="titleMedium" style={{ marginRight: 16 }}>
              Tower: {selectedTower.name}
            </Text>
          )}
          {lastUpdated && (
            <Text variant="bodyMedium" style={{ marginRight: 16 }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Text>
          )}
          <Button
            mode="contained"
            onPress={handleRefresh}
            loading={isLoading}
            icon={({ size, color }) => <RefreshCw size={size} color={color} />}
          >
            Refresh
          </Button>
        </View>
      </View>

      <SegmentedButtons
        value={activeTab}
        onValueChange={setActiveTab}
        buttons={[
          { value: "overview", label: "Overview" },
          { value: "forecast", label: "Forecast" },
          { value: "alerts", label: "Alerts" },
          { value: "summary", label: "Summary" },
        ]}
        style={styles.segmentedButtons}
      />

      <View style={styles.tabContent}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={{ marginTop: 16 }}>Loading weather data...</Text>
          </View>
        ) : (
          <>
            {activeTab === "overview" && (
              <WeatherOverviewTab
                weatherData={weatherData}
                selectedTower={selectedTower}
                towers={towers}
                onTowerChange={handleTowerChange}
              />
            )}
            {activeTab === "forecast" && (
              <WeatherForecastTab forecastData={forecastData} selectedTower={selectedTower} />
            )}
            {activeTab === "alerts" && <WeatherAlertsTab weatherData={weatherData} forecastData={forecastData} />}
            {activeTab === "summary" && (
              <WeatherSummaryTab towers={towers} onTowerSelect={handleTowerChange} selectedTower={selectedTower} />
            )}
          </>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: "100%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  segmentedButtons: {
    margin: 16,
  },
  tabContent: {
    flex: 1,
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
})

